(() => {
  if (customElements.get('zr-recoil-wall')) return;
  class RecoilWall extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.shone = new Set();
      this.members = []; this.filter = 'all'; this.limit = 24;
      this.list = this.querySelector('.rw__wall');
      this.status = this.querySelector('.rw__status');
      this.form = this.querySelector('form');
      this.formStatus = this.querySelector('.rw__form-status');
      this.search = this.querySelector('input[type=search]');
      this.more = this.querySelector('.rw__more');
      this.search.addEventListener('input', () => { this.limit = 24; this.render(); });
      this.querySelectorAll('[data-filter]').forEach(button => button.addEventListener('click', () => {
        this.filter = button.dataset.filter; this.limit = 24;
        this.querySelectorAll('[data-filter]').forEach(b => b.setAttribute('aria-pressed', String(b === button)));
        this.render();
      }));
      this.more.addEventListener('click', () => { this.limit += 24; this.render(); });
      this.form.addEventListener('submit', event => this.join(event));
      this.load();
    }
    get api() {
      try { const u = new URL(this.dataset.api); return u.protocol === 'https:' && !u.username && !u.password ? u.origin + u.pathname.replace(/\/$/, '') : ''; }
      catch { return ''; }
    }
    async request(path, options = {}) {
      const response = await fetch(this.api + path, { ...options, credentials: 'omit', signal: AbortSignal.timeout(60000) });
      if (!response.ok) throw new Error('request_failed');
      return response.json();
    }
    async load() {
      if (!this.api) {
        this.status.textContent = this.dataset.textLoading;
        this.formStatus.textContent = this.dataset.textUnavailable;
        try {
          const response = await fetch(this.dataset.members, {credentials: 'omit', signal: AbortSignal.timeout(15000)});
          if (!response.ok) throw new Error('wall_unavailable');
          const data = await response.json();
          if (!Array.isArray(data.members)) throw new Error('invalid_wall');
          this.members = data.members.filter(m => typeof m.name === 'string' && Array.isArray(m.games));
          this.render();
        } catch { this.status.textContent = this.dataset.textError; }
        return;
      }
      this.status.textContent = this.dataset.textLoading;
      try {
        const data = await this.request('/v1/wall');
        if (!Array.isArray(data.members)) throw new Error('invalid_response');
        this.members = data.members.filter(m => typeof m.name === 'string' && Array.isArray(m.games));
        this.render();
      } catch { this.status.textContent = this.dataset.textError; }
    }
    render() {
      const query = this.search.value.trim().toLocaleLowerCase();
      const filtered = this.members.filter(m => m.name.toLocaleLowerCase().includes(query) && (this.filter === 'all' || m.games.includes(this.filter)));
      const fragment = document.createDocumentFragment();
      for (const member of filtered.slice(0, this.limit)) {
        const node = this.querySelector('template').content.cloneNode(true);
        const plate = node.querySelector('.rw__member');
        let hash = 2166136261;
        const identity = member.name + '|' + member.games.join('|');
        for (const char of identity) hash = Math.imul(hash ^ char.codePointAt(0), 16777619) >>> 0;
        const width = 76 + (hash % 12);
        const group = Math.floor(this.members.indexOf(member) / 8);
        const slots = [1, 2, 3, 4, 5, 6, 7, 8];
        let seed = (0x9e3779b9 ^ Math.imul(group + 1, 2654435761)) >>> 0;
        for (let i = slots.length - 1; i > 0; i--) {
          seed ^= seed << 13; seed ^= seed >>> 17; seed ^= seed << 5;
          const j = (seed >>> 0) % (i + 1);
          [slots[i], slots[j]] = [slots[j], slots[i]];
        }
        plate.dataset.member = identity;
        plate.style.setProperty('--rw-col', String(slots[this.members.indexOf(member) % 8]));
        plate.style.setProperty('--rw-desktop-width', (365 + ((hash >>> 5) % 66)) + 'px');
        plate.style.setProperty('--rw-mobile-width', width + '%');
        plate.style.setProperty('--rw-offset', ((hash >>> 9) % (101 - width)) + '%');
        plate.style.setProperty('--rw-lift', ((hash >>> 16) % 15) + 'px');
        node.querySelector('.rw__name').textContent = member.name;
        node.querySelector('.rw__games').textContent = member.games.filter(g => ['valorant', 'gta'].includes(g)).map(g => g.toUpperCase()).join(' · ');
        fragment.append(node);
      }
      this.shineObserver?.disconnect();
      this.list.replaceChildren(fragment);
      this.observeShine();
      this.status.textContent = filtered.length ? '' : this.dataset.textEmpty;
      this.more.hidden = filtered.length <= this.limit;
      if (!this.animated && filtered.length) { this.list.classList.add('is-entering'); this.animated = true; }
    }
    disconnectedCallback() { this.shineObserver?.disconnect(); }
    observeShine() {
      if (!('IntersectionObserver' in window) || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.shineObserver = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const tag = entry.target;
          this.shineObserver.unobserve(tag);
          if (this.shone.has(tag.dataset.member)) continue;
          this.shone.add(tag.dataset.member);
          tag.classList.add('is-shining');
          tag.addEventListener('animationend', () => tag.classList.remove('is-shining'), {once:true});
        }
      }, {threshold:0.4});
      this.list.querySelectorAll('.rw__member').forEach(tag => {
        if (!this.shone.has(tag.dataset.member)) this.shineObserver.observe(tag);
      });
    }
    async join(event) {
      event.preventDefault();
      if (!this.form.reportValidity()) return;
      if (!this.api) { this.formStatus.textContent = this.dataset.textUnavailable; return; }
      const button = this.form.querySelector('button');
      button.disabled = true; this.form.setAttribute('aria-busy', 'true');
      this.formStatus.textContent = this.dataset.textLoading;
      try {
        const values = new FormData(this.form);
        const data = await this.request('/v1/wall/join', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({orderNumber: values.get('orderNumber').trim(), email: values.get('email').trim(), consent: values.get('consent') === 'on'})
        });
        if (data.ok !== true) throw new Error('unverified');
        this.form.reset(); this.formStatus.textContent = this.dataset.textSuccess;
        this.search.value = ''; this.filter = 'all'; this.limit = 24;
        this.querySelectorAll('[data-filter]').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.filter === 'all')));
        await this.load();
      } catch { this.formStatus.textContent = this.dataset.textFailed; }
      finally { button.disabled = false; this.form.removeAttribute('aria-busy'); }
    }
  }
  customElements.define('zr-recoil-wall', RecoilWall);
})();
